


import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const GrowthVisibility = () => {
  return (
    <div className="font-['Raleway',sans-serif] bg-white text-gray-900 overflow-x-hidden pt-[90px]">
      
      <Navbar />

      {/*  HERO SECTION  */}
      <section className="relative min-h-[300px] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&q=80')`,
          }}
        />
        {/* Dark Blue Overlay */}
        <div className="absolute inset-0 bg-[#19354a]/90" />
        
        {/* Content */}
        <div className="relative z-10 text-center px-6">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8"
          >
            Patient Growth & Visibility
          </motion.h1>
          
          {/* Breadcrumb Buttons */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center justify-center gap-4 flex-wrap"
          >
            <Link 
              to="/" 
              className="px-8 py-3 bg-[#39a5d3] text-white rounded-full font-semibold hover:bg-[#2a8db8] transition-all"
            >
              Home
            </Link>
            <span className="text-white text-2xl">|</span>
            <Link 
              to="/services" 
              className="px-8 py-3 bg-[#39a5d3] text-white rounded-full font-semibold hover:bg-[#2a8db8] transition-all"
            >
              Services
            </Link>
            <span className="text-white text-2xl">|</span>
            <span className="px-8 py-3 bg-[#39a5d3] text-white rounded-full font-semibold">
              Patient Growth & Visibility
            </span>
          </motion.div>
        </div>
      </section>

      {/*  CONTENT SECTION  */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left - Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="/images/meta3.png" 
                alt="Patient Growth & Visibility"
                className="w-full max-w-[550px] h-auto mx-auto"
              />
            </motion.div>

            {/* Right - Content */}
            <div>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-gray-700 leading-relaxed mb-6"
              >
                In today's competitive healthcare landscape, online visibility is everything for doctors and medical practices. Our Patient Growth & Visibility services are specifically designed to put your practice in front of patients actively searching for healthcare services. We combine proven SEO strategies, local optimization, and reputation management to ensure your practice stands out when patients need you most.
              </motion.p>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-gray-700 leading-relaxed mb-6"
              >
                From <strong className="text-[#19354a]">healthcare-focused SEO</strong> to technical optimization, we cover every aspect of medical practice visibility. Our local SEO expertise helps doctors and clinics dominate their geographic markets, while our Google My Business optimization ensures you capture local patients actively searching for medical services in your area.
              </motion.p>  

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-gray-700 leading-relaxed mb-8"
              >
                At Docmize, we understand that sustainable patient growth comes from a solid digital foundation. Our data-driven approach to medical keyword research, competitor analysis, and patient review management ensures your practice not only ranks higher but also builds trust with potential patients searching for quality healthcare.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Link 
                  to="/contact" 
                  className="inline-block px-8 py-4 bg-[#19354a] text-white rounded-lg font-semibold hover:bg-[#0f2133] transition-all"
                >
                  Boost Your Practice Visibility
                </Link>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      {/*  WHY CHOOSE US - BLUE SECTION  */}
      <section className="relative py-20 overflow-hidden">
        {/* Dark Blue Background */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #0f1c3f 0%, #1a2d5e 50%, #152349 100%)'
          }}
        />
        
        {/* Diagonal Lines Pattern */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 50px,
                rgba(59, 130, 246, 0.1) 50px,
                rgba(59, 130, 246, 0.1) 52px
              )`
            }}
          />
          <div className="absolute top-0 left-[20%] w-[2px] h-full bg-gradient-to-b from-transparent via-blue-500/20 to-transparent transform rotate-[30deg] origin-top"></div>
          <div className="absolute top-0 left-[40%] w-[2px] h-full bg-gradient-to-b from-transparent via-blue-400/30 to-transparent transform rotate-[30deg] origin-top"></div>
          <div className="absolute top-0 left-[60%] w-[3px] h-full bg-gradient-to-b from-transparent via-blue-500/20 to-transparent transform rotate-[30deg] origin-top"></div>
          <div className="absolute top-0 left-[80%] w-[2px] h-full bg-gradient-to-b from-transparent via-blue-400/15 to-transparent transform rotate-[30deg] origin-top"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4">
              Why Choose Our Healthcare SEO & Visibility Services?
            </h2>
            <p className="text-white/70 text-lg">
              Here's why doctors and healthcare providers trust us with their patient growth:
            </p>
          </motion.div>

          {/* Benefits Grid - 2 Columns */}
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-8 max-w-5xl mx-auto">
            
            {/* Left Column */}
            <div className="space-y-8">
              <motion.div 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex items-start gap-4"
              >
                <span className="text-[#39a5d3] text-2xl font-bold">»</span>
                <p className="text-white text-lg">Healthcare-specific SEO strategies optimized for medical practices</p>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex items-start gap-4"
              >
                <span className="text-[#39a5d3] text-2xl font-bold">»</span>
                <p className="text-white text-lg">Local SEO to attract patients in your clinic's service area</p>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex items-start gap-4"
              >
                <span className="text-[#39a5d3] text-2xl font-bold">»</span>
                <p className="text-white text-lg">Google My Business optimization for medical practices</p>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex items-start gap-4"
              >
                <span className="text-[#39a5d3] text-2xl font-bold">»</span>
                <p className="text-white text-lg">Medical directory listings and NAP consistency management</p>
              </motion.div>
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              <motion.div 
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex items-start gap-4"
              >
                <span className="text-[#39a5d3] text-2xl font-bold">»</span>
                <p className="text-white text-lg">Healthcare review and reputation management strategies</p>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex items-start gap-4"
              >
                <span className="text-[#39a5d3] text-2xl font-bold">»</span>
                <p className="text-white text-lg">Medical keyword research targeting patient search behavior</p>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex items-start gap-4"
              >
                <span className="text-[#39a5d3] text-2xl font-bold">»</span>
                <p className="text-white text-lg">HIPAA-compliant Growth Ecosystem practices</p>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex items-start gap-4"
              >
                <span className="text-[#39a5d3] text-2xl font-bold">»</span>
                <p className="text-white text-lg">Monthly reporting with patient acquisition metrics and insights</p>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      {/*  OUR SERVICES SECTION  */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-[#19354a] mb-4">
              Our Patient Growth & Visibility Services
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive healthcare SEO solutions to help doctors dominate local search
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Service 1 - ORANGE BORDER */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a]] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#19354a] mb-3">SEO for Doctors</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Complete healthcare SEO optimization to boost your medical practice rankings.
              </p>
            </motion.div>

            {/* Service 2 - BLUE BORDER */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#19354a] mb-3">Local SEO</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Dominate local search results and attract patients in your clinic area.
              </p>
            </motion.div>

            {/* Service 3 - ORANGE BORDER */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a]] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#19354a] mb-3">Google My Business</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Optimize your GMB profile for single or multi-location medical practices.
              </p>
            </motion.div>

            {/* Service 4 - BLUE BORDER */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#19354a] mb-3">Medical Directory Listings</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Ensure consistent practice information across all medical directories.
              </p>
            </motion.div>

            {/* Service 5 - BLUE BORDER */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#19354a] mb-3">Online Reputation Management</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Build and protect your medical practice's online reputation.
              </p>
            </motion.div>

            {/* Service 6 - BLUE BORDER */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#19354a] mb-3">Review & Rating Growth</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Strategic patient review management to boost your practice ratings.
              </p>
            </motion.div>

            {/* Service 7 - BLUE BORDER */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.7 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#19354a] mb-3">Voice Search Optimization</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Optimize for voice search and question-based patient queries.
              </p>
            </motion.div>

            {/* Service 8 - BLUE BORDER */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.8 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#19354a] mb-3">Multi-Location Clinic SEO</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Specialized SEO strategies for healthcare practices with multiple locations.
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/*  BENEFITS SECTION  */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-[#19354a] mb-4">
              Reap the Benefits of Healthcare SEO Services
            </h2>
            <h3 className="text-2xl lg:text-3xl font-bold text-[#19354a] mb-6">
              at <span className="text-[#19354a]]">Docmize</span>
            </h3>
            <p className="text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Our comprehensive healthcare SEO and visibility strategies are designed to deliver sustainable patient growth. We focus on long-term results that keep your medical practice ahead of the competition.
            </p>
          </motion.div>

          {/* Benefit 1 - Image Right */}
          <div className="grid lg:grid-cols-2 gap-0 mb-8">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#fff4e6] p-6 lg:p-10 flex items-center border-l-4 border-[#19354a]"
            >
              <div>
                <h3 className="text-2xl lg:text-3xl font-bold text-[#19354a] mb-4">
                  Medical Keyword Strategy & Research
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We identify high-value medical keywords that patients are searching for when looking for healthcare services. Our comprehensive research uncovers patient search patterns your competitors are missing, giving you the edge in local medical search rankings.
                </p>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-[#e8f4fc]"
            >
              <img 
                src="/images/ppcseo.png" 
                alt="Medical Keyword Research"
                className="w-full h-full min-h-[280px] object-cover"
              />
            </motion.div>
          </div>

          {/* Benefit 2 - Image Left */}
          <div className="grid lg:grid-cols-2 gap-0 mb-8">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#e6f7f0] order-2 lg:order-1"
            >
              <img 
                src="/images/ppc goole.png" 
                alt="Healthcare On-Page Optimization"
                className="w-full h-full min-h-[280px] object-cover"
              />
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-[#e6f2e6] p-6 lg:p-10 flex items-center order-1 lg:order-2 border-r-4 border-[#E85A2C]"
            >
              <div>
                <h3 className="text-2xl lg:text-3xl font-bold text-[#E85A2C] mb-4">
                  Healthcare Website Optimization
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We optimize every element of your medical website from healthcare-specific meta tags and patient-focused content to site speed and mobile responsiveness. Our technical audits ensure search engines can effectively crawl and index your practice website.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Benefit 3 - Image Right */}
          <div className="grid lg:grid-cols-2 gap-0 mb-8">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#f0e6f7] p-6 lg:p-10 flex items-center border-l-4 border-[#19354a]"
            >
              <div>
                <h3 className="text-2xl lg:text-3xl font-bold text-[#19354a] mb-4">
                  Local Patient Acquisition
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  For medical practices targeting local patients, we optimize your Google My Business profile, build healthcare directory citations, and ensure practice information consistency. Get found by patients actively searching for doctors in your area.
                </p>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-[#e6e9f7]"
            >
              <img 
                src="/images/meta5.png" 
                alt="Local Healthcare SEO"
                className="w-full h-full min-h-[280px] object-cover"
              />
            </motion.div>
          </div>

          {/* Benefit 4 - Image Left */}
          <div className="grid lg:grid-cols-2 gap-0">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#fce8e8] order-2 lg:order-1"
            >
              <img 
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80" 
                alt="Patient Analytics & Reporting"
                className="w-full h-full min-h-[280px] object-cover"
              />
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-[#fff0e6] p-6 lg:p-10 flex items-center order-1 lg:order-2 border-r-4 border-[#E85A2C]"
            >
              <div>
                <h3 className="text-2xl lg:text-3xl font-bold text-[#E85A2C] mb-4">
                  Monitor & Improve Patient Acquisition
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Healthcare SEO is an ongoing process. We continuously monitor your search rankings, analyze patient acquisition data, and make strategic adjustments to ensure sustained growth and improved online visibility for your medical practice.
                </p>
              </div>
            </motion.div>
          </div>

        </div>
      </section>


      {/*  ENTRUST US SECTION  */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-[#19354a] mb-2">
                Entrust us to Scale
              </h2>
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-[#19354a] mb-4">
                Your Practice Rankings,
              </h2>
              <h3 className="text-2xl lg:text-3xl font-bold text-[#19354a] mb-2">
                Outrank Competing Clinics
              </h3>
              <h3 className="text-2xl lg:text-3xl font-bold text-[#19354a] mb-8">
                and Grow Your Patient Base.
              </h3>

              {/* Checklist Items */}
              <div className="space-y-5">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-6 h-6 bg-[#E85A2C] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Create patient-focused content that drives appointments and helps your practice rank on page 1 for local medical searches.
                  </p>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-6 h-6 bg-[#E85A2C] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    We utilize proven healthcare marketing strategies that deliver lasting patient growth, not quick fixes that fade away.
                  </p>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-6 h-6 bg-[#E85A2C] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Stay informed about your patient acquisition campaigns with professional tools like Google Analytics and medical practice dashboards.
                  </p>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.4 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-6 h-6 bg-[#E85A2C] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Prioritize and plan as you like. As <span className="text-[#19354a] font-semibold">healthcare marketing specialists</span>, we keep you in the loop and provide feedback on areas you wish to focus on for practice growth.
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Right - Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center"
            >
              <img 
                src="/images/services-1.png" 
                alt="Scale Your Practice Rankings"
                className="w-full max-w-[500px] h-auto"
              />
            </motion.div>

          </div>
        </div>
      </section>

      {/*  FAQ SECTION  */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-[#19354a]">
              Frequently Asked Questions about
            </h2>
            <h3 className="text-3xl lg:text-4xl font-bold text-[#19354a]">
              Healthcare SEO & Visibility Services.
            </h3>
          </motion.div>

          {/* FAQ Content - Image Left, Questions Right */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Left - Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex justify-center lg:sticky lg:top-24"
            >
              <img 
                src="/images/faq-img.png" 
                alt="FAQ Healthcare SEO"
                className="w-full max-w-[250px] h-auto"
              />
            </motion.div>

            {/* Right - FAQ Items */}
            <div className="space-y-4">
              {[
                {
                  question: "How long does it take to see results from healthcare SEO?",
                  answer: "Healthcare SEO is a long-term strategy. While some improvements in local search visibility can be seen within 4-6 weeks, significant patient acquisition results typically take 3-6 months. Factors like local competition, current online presence, and specialty affect the timeline."
                },
                {
                  question: "Why is local SEO important for my medical practice?",
                  answer: "Over 77% of patients use search engines before booking appointments. Local SEO ensures your practice appears when patients in your area search for doctors or medical services. It's crucial for attracting nearby patients actively looking for healthcare providers."
                },
                {
                  question: "Can you guarantee first-page rankings for my practice?",
                  answer: "No ethical healthcare SEO company can guarantee specific rankings as search algorithms constantly change. What we guarantee is implementing proven medical SEO strategies, continuous optimization, transparent reporting, and measurable improvements in your online visibility and patient acquisition."
                },
                {
                  question: "How do you handle patient reviews and reputation management?",
                  answer: "We implement strategic review generation campaigns, monitor your online reputation across platforms, respond professionally to reviews (following HIPAA guidelines), and help build trust with potential patients through positive testimonials and ratings."
                },
                {
                  question: "What reports will I receive about my practice's SEO performance?",
                  answer: "You'll receive monthly comprehensive reports including local keyword rankings, organic traffic growth, patient inquiry metrics, Google My Business insights, competitor analysis, and actionable recommendations. We also provide real-time dashboard access for tracking your practice growth."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <details className="group">
                    <summary className="flex items-center justify-between p-5 bg-[#19354a] rounded-lg cursor-pointer list-none">
                      <span className="font-semibold text-white">{faq.question}</span>
                      <span className="text-white text-2xl font-light group-open:hidden">+</span>
                      <span className="text-white text-2xl font-light hidden group-open:block">−</span>
                    </summary>
                    <div className="px-5 py-4 bg-gray-50 rounded-b-lg text-gray-700 leading-relaxed -mt-2">
                      {faq.answer}
                    </div>
                  </details>
                </motion.div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/*  CTA SECTION  */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-4xl font-bold text-[#19354a] mb-6"
          >
            Ready to Grow Your Patient Base?
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-600 text-lg mb-8"
          >
            Let our healthcare SEO experts help you achieve sustainable practice growth and maximum online visibility.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-[#19354a] text-white rounded-lg font-semibold hover:bg-[#0f2133] transition-all"
            >
              Get a Free Practice SEO Audit
            </Link>
            <a 
              href="tel:+918920845297" 
              className="px-8 py-4 border-2 border-[#19354a] text-[#19354a] rounded-lg font-semibold hover:bg-[#19354a] hover:text-white transition-all"
            >
              Call Us Now
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GrowthVisibility;